/**
 * Feature hook: Upload IFS document to OneDrive (Download-to-Teams).
 * Success message is set in slice state for use in dialog (no loading UI).
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  uploadDocumentToOneDrive,
  uploadTemplateToOneDrive,
  closeDownloadSuccessDialog,
} from "../slices/documents/uploadToOneDriveSlice";
import { useLogin } from "./useLogin";

export function useUploadToOneDrive() {
  const dispatch = useAppDispatch();
  const { teamsContext } = useLogin();
  const uploadState = useAppSelector((state) => state.UploadToOneDrive);

  const uploadDocument = useCallback(
    (item, contextOrOneDriveRoot) => {
      if (!item) return;
      const hasOverride =
        contextOrOneDriveRoot &&
        typeof contextOrOneDriveRoot === "object" &&
        contextOrOneDriveRoot.tenantId != null;
      let teamId, channelId, tenantId, oneDriveRoot;
      if (hasOverride) {
        teamId = contextOrOneDriveRoot.teamId ?? "";
        channelId = contextOrOneDriveRoot.channelId ?? "";
        tenantId = contextOrOneDriveRoot.tenantId;
        oneDriveRoot = contextOrOneDriveRoot.oneDriveRoot;
      } else {
        if (!teamsContext?.user?.tenant?.id) return;
        teamId =
          teamsContext.channel != null
            ? teamsContext.channel.membershipType === "Private"
              ? teamsContext.channel.ownerGroupId
              : teamsContext.team?.groupId
            : "";
        channelId =
          teamsContext.channel != null ? teamsContext.channel.id : "";
        tenantId = teamsContext.user.tenant.id;
        oneDriveRoot =
          typeof contextOrOneDriveRoot === "string"
            ? contextOrOneDriveRoot
            : channelId
            ? "ChannelDoc"
            : "MainMenu";
      }
      dispatch(
        uploadDocumentToOneDrive({
          item,
          teamId,
          channelId,
          tenantId,
          oneDriveRoot,
        })
      );
    },
    [dispatch, teamsContext]
  );

  const uploadTemplate = useCallback(
    (template, contextOverride) => {
      if (!template) {
        return Promise.reject(new Error("No template selected"));
      }
      const hasOverride =
        contextOverride &&
        typeof contextOverride === "object" &&
        contextOverride.tenantId != null;
      let teamId, channelId, tenantId, oneDriveRoot;
      if (hasOverride) {
        teamId = contextOverride.teamId ?? "";
        channelId = contextOverride.channelId ?? "";
        tenantId = contextOverride.tenantId;
        oneDriveRoot = contextOverride.oneDriveRoot;
      } else {
        if (!teamsContext?.user?.tenant?.id) {
          return Promise.reject(new Error("No tenant context"));
        }
        teamId =
          teamsContext.channel != null
            ? teamsContext.channel.membershipType === "Private"
              ? teamsContext.channel.ownerGroupId
              : teamsContext.team?.groupId
            : "";
        channelId =
          teamsContext.channel != null ? teamsContext.channel.id : "";
        tenantId = teamsContext.user.tenant.id;
        oneDriveRoot =
          typeof contextOverride === "string"
            ? contextOverride
            : channelId
            ? "ChannelDoc"
            : "MainMenu";
      }
      return dispatch(
        uploadTemplateToOneDrive({
          template,
          teamId,
          channelId,
          tenantId,
          oneDriveRoot,
        })
      );
    },
    [dispatch, teamsContext]
  );

  return {
    state: {
      loading: uploadState?.loading ?? false,
      error: uploadState?.error ?? null,
      message: uploadState?.message ?? "",
      downloadSuccessDialogOpen: uploadState?.downloadSuccessDialogOpen ?? false,
    },
    uploadDocumentToOneDrive: uploadDocument,
    uploadTemplateToOneDrive: uploadTemplate,
    closeDownloadSuccessDialog: () => dispatch(closeDownloadSuccessDialog()),
  };
}
